import AdminLayout from "@/components/layout/AdminLayout";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { MediaForm } from "@/components/media/MediaForm";
import { MediaList } from "@/components/media/MediaList";
import { PlusIcon } from "lucide-react";
import { useToast } from "@/hooks/use-toast";

export default function Media() {
  const [search, setSearch] = useState("");
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [selectedMedia, setSelectedMedia] = useState<any>(null);
  const queryClient = useQueryClient();
  const { toast } = useToast();

  const { data: mediaList, isLoading } = useQuery({
    queryKey: ["media-list", search],
    queryFn: async () => {
      console.log("Fetching media with search:", search);
      let query = supabase
        .from("media")
        .select("*")
        .order("media_name");

      if (search) {
        query = query.ilike("media_name", `%${search}%`);
      }

      const { data, error } = await query;

      if (error) {
        console.error("Error fetching media:", error);
        throw error;
      }

      return data;
    },
  });

  const handleSubmit = async (formData: any) => {
    console.log("Submitting media data:", formData);
    try {
      if (selectedMedia) {
        const { error } = await supabase
          .from("media")
          .update(formData)
          .eq("media_id", selectedMedia.media_id);

        if (error) throw error;
        toast({
          title: "Success",
          description: "Media updated successfully",
        });
      } else {
        const { error } = await supabase
          .from("media")
          .insert(formData);

        if (error) throw error;
        toast({
          title: "Success",
          description: "Media added successfully",
        });
      }

      queryClient.invalidateQueries({ queryKey: ["media-list"] });
      setIsDialogOpen(false);
      setSelectedMedia(null);
    } catch (error) {
      console.error("Error saving media:", error);
      toast({
        title: "Error",
        description: "Failed to save media",
        variant: "destructive",
      });
    }
  };

  const handleEdit = (media: any) => {
    setSelectedMedia(media);
    setIsDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setIsDialogOpen(false);
    setSelectedMedia(null);
  };

  return (
    <AdminLayout>
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <h1 className="text-3xl font-bold">Media</h1>
          <Button onClick={() => setIsDialogOpen(true)}>
            <PlusIcon className="h-4 w-4 mr-2" />
            Add New Media
          </Button>
        </div>

        <div className="flex items-center space-x-2">
          <Input
            placeholder="Search media..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="max-w-sm"
          />
        </div>

        {isLoading ? (
          <div>Loading media...</div>
        ) : (
          <MediaList
            media={mediaList || []}
            onEdit={handleEdit}
          />
        )}

        <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>
                {selectedMedia ? "Edit Media" : "Add New Media"}
              </DialogTitle>
            </DialogHeader>
            <MediaForm
              initialData={selectedMedia}
              onSubmit={handleSubmit}
              onCancel={handleCloseDialog}
            />
          </DialogContent>
        </Dialog>
      </div>
    </AdminLayout>
  );
}